import { Phone, Mail, MapPin, User, CreditCard, FileText, Navigation } from 'lucide-react';
import { cur } from '@/lib/currency';

function InfoRow({ icon: Icon, label, value, href }) {
  if (!value) return null;
  const content = (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
}

function Section({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <h3 className="font-bold text-sm text-foreground mb-1">{title}</h3>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

export default function InfoTab({ customer }) {
  if (!customer) return null;

  const phone = customer.phone || customer.mobile;
  const city = [customer.street, customer.city, Array.isArray(customer.state_id) ? customer.state_id[1] : customer.state_id]
    .filter(Boolean).join('، ');
  const salesperson = Array.isArray(customer.user_id) ? customer.user_id[1] : customer.user_id;
  const team = Array.isArray(customer.team_id) ? customer.team_id[1] : customer.team_id;
  const paymentTerm = Array.isArray(customer.property_payment_term_id) ? customer.property_payment_term_id[1] : customer.property_payment_term_id;

  const hasCoords = customer.partner_latitude && customer.partner_longitude;
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${customer.partner_latitude},${customer.partner_longitude}`
    : city ? `https://www.google.com/maps/search/${encodeURIComponent(city)}` : null;

  return (
    <div className="space-y-3">
      {/* Contact */}
      <Section title="📞 معلومات التواصل">
        <InfoRow icon={Phone} label="الهاتف" value={phone} href={phone ? `tel:${phone}` : undefined} />
        <InfoRow icon={Phone} label="الجوال" value={customer.mobile} href={customer.mobile ? `tel:${customer.mobile}` : undefined} />
        <InfoRow icon={Mail} label="البريد الإلكتروني" value={customer.email} href={customer.email ? `mailto:${customer.email}` : undefined} />
        <InfoRow icon={FileText} label="الكود المرجعي" value={customer.ref} />
      </Section>

      {/* Location */}
      <Section title="📍 العنوان">
        <InfoRow icon={MapPin} label="العنوان الكامل" value={city || '—'} />
        {hasCoords && (
          <div className="flex items-center gap-3 py-3">
            <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
              <Navigation className="w-4 h-4 text-muted-foreground" />
            </div>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
              className="text-sm font-medium text-primary flex items-center gap-1.5">
              <Navigation className="w-3.5 h-3.5" /> فتح خرائط جوجل
            </a>
          </div>
        )}
        {hasCoords && (
          <InfoRow icon={MapPin} label="إحداثيات GPS"
            value={`${customer.partner_latitude?.toFixed(5)}, ${customer.partner_longitude?.toFixed(5)}`} />
        )}
      </Section>

      {/* Business */}
      <Section title="💼 معلومات الأعمال">
        <InfoRow icon={User} label="المندوب المسؤول" value={salesperson} />
        <InfoRow icon={User} label="فريق المبيعات" value={team} />
        <InfoRow icon={CreditCard} label="شروط الدفع" value={paymentTerm} />
        <InfoRow icon={FileText} label="الرقم الضريبي" value={customer.vat} />
      </Section>

      {/* Financial */}
      <Section title="💰 المعلومات المالية">
        <InfoRow icon={CreditCard} label="حد الائتمان"
          value={customer.credit_limit ? `${Number(customer.credit_limit).toLocaleString('ar-EG')} ${cur()}` : 'غير محدد'} />
        <InfoRow icon={CreditCard} label="الرصيد الدائن"
          value={customer.credit ? `${Number(customer.credit).toLocaleString('ar-EG')} ${cur()}` : `0 ${cur()}`} />
        <InfoRow icon={CreditCard} label="المستحقات"
          value={customer.debit ? `${Number(customer.debit).toLocaleString('ar-EG')} ${cur()}` : `0 ${cur()}`} />
      </Section>

      {/* Notes */}
      {customer.comment && (
        <Section title="📝 ملاحظات داخلية">
          <div className="py-2">
            <p className="text-sm text-foreground leading-relaxed">{customer.comment}</p>
          </div>
        </Section>
      )}
    </div>
  );
}